import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import Gallery from 'react-photo-gallery';
import { Spinner, designSystem } from '@yola/ws-ui';
import StockGalleryItem from './stock-gallery-item';
import StockGallerySearch from './stock-gallery-search';
import StockGalleryEmptyState from './stock-gallery-empty-state';
import ScrollableContent from './scrollable-content';

const { Box } = designSystem;

function StockGalleryView({
  items,
  searchQuery = '',
  searchPlaceholder = '',
  isNotFound,
  isLoading,
  isMobile,
  isSearching = false,
  onSearch,
  onFocus,
  onLoadMoreItems,
  onItemClick,
}) {
  const renderImage = useCallback(
    ({ photo: { id, src, width, height, info, isSelected } }) => (
      <StockGalleryItem
        key={id}
        id={id}
        src={src}
        width={width}
        height={height}
        info={info}
        isSelected={isSelected}
        isSearching={isSearching}
        isMobile={isMobile}
        onClick={onItemClick}
      />
    ),
    [onItemClick, isSearching, isMobile]
  );

  return (
    <div className="ws-stock-gallery-dialog__gallery-content">
      <StockGallerySearch
        searchQuery={searchQuery}
        searchPlaceholder={searchPlaceholder}
        isMobile={isMobile}
        onSearch={onSearch}
        onFocus={onFocus}
      />
      <ScrollableContent onLoadMore={onLoadMoreItems}>
        <Box padding={isMobile ? 'spacing-xs' : 'spacing-m'} paddingTop="spacing-xs">
          {isNotFound && <StockGalleryEmptyState searchQuery={searchQuery} />}
          <Gallery photos={items} renderImage={renderImage} />
          {isLoading && <Spinner containerPosition="bottom" spinnerPosition="center" />}
        </Box>
      </ScrollableContent>
    </div>
  );
}

const photoItemShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  alt: PropTypes.string,
  info: PropTypes.shape({
    authorName: PropTypes.string,
    authorLink: PropTypes.string,
    stockLink: PropTypes.string,
    stockName: PropTypes.string,
  }),
  isSelected: PropTypes.bool.isRequired,
  isLoaded: PropTypes.bool,
});

StockGalleryView.propTypes = {
  items: PropTypes.arrayOf(photoItemShape).isRequired,
  searchQuery: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  isNotFound: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isSearching: PropTypes.bool,
  onSearch: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onLoadMoreItems: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
};

export default StockGalleryView;
